import React, { useState, useEffect, useRef } from "react";

import api from "../../services/api";

import { User } from "../../models/entities/user";
import { Course, UserEvent } from "../../models/entities/twygo";

import { Spin, Tooltip, Empty, message } from "antd";
import {
  StyledDrawer,
  Container,
  UsersListContainer,
  UsersListTitle,
  UsersCount,
  CoursesContainer,
  CoursesTitle,
  StyledTable,
  LoadingContainer,
  FooterContainer,
  EmptyContainer,
  Button,
} from "./styles";

interface TwygoMassiveCoursesProps {
  isVisible: boolean;
  onClose: () => void;
  selectedUsers: number[];
  users: User[];
  onOpenLogsModal: (
    logs: Array<{ message: string; type: "success" | "error" | "info" }>,
    progressPercent: number,
    title?: string
  ) => void;
}

const TwygoMassiveCourses: React.FC<TwygoMassiveCoursesProps> = ({
  isVisible,
  onClose,
  selectedUsers,
  users,
  onOpenLogsModal,
}) => {
  const [loadingBulkCourses, setLoadingBulkCourses] = useState(false);
  const [selectedCoursesForBulk, setSelectedCoursesForBulk] = useState<
    number[]
  >([]);
  const [loadingLinkedUsers, setLoadingLinkedUsers] = useState(false);
  const [linkedUsers, setLinkedUsers] = useState<{ [key: number]: string[] }>(
    {}
  );
  const [availableCoursesForBulk, setAvailableCoursesForBulk] = useState<
    Course[]
  >([]);
  const [progressPercent, setProgressPercent] = useState(0);

  const logsRef = useRef<
    Array<{ message: string; type: "success" | "error" | "info" }>
  >([]);

  const addLog = (message: string, type: "success" | "error" | "info") => {
    logsRef.current = [...logsRef.current, { message, type }];
    onOpenLogsModal([...logsRef.current], progressPercent);
  };

  const updateProgress = (progress: number) => {
    setProgressPercent(progress);
    onOpenLogsModal([...logsRef.current], progress);
  };

  useEffect(() => {
    if (!isVisible) {
      logsRef.current = [];
      setProgressPercent(0);
    }
  }, [isVisible]);

  const handleLoadCoursesData = async () => {
    if (!isVisible || selectedUsers.length === 0) return;

    setLoadingBulkCourses(true);
    setLoadingLinkedUsers(true);
    setSelectedCoursesForBulk([]);

    try {
      const coursesResponse = await api.get("/twygo/v2/courses");
      const allCourses = coursesResponse.data.data.contents || [];
      setAvailableCoursesForBulk(allCourses);

      const newLinkedUsers: { [key: number]: string[] } = {};

      for (const userId of selectedUsers) {
        const user = users.find((u) => u.id === userId);
        if (!user?.user_twygo_id) continue;

        try {
          const userEventsResponse = await api.get(
            `/twygo/info-user-courses/${user.user_twygo_id}`
          );
          const userCourses = userEventsResponse.data.events || [];

          userCourses.forEach((course: UserEvent) => {
            if (!newLinkedUsers[course.event_id]) {
              newLinkedUsers[course.event_id] = [];
            }
            newLinkedUsers[course.event_id].push(user.full_name || "");
          });
        } catch (error) {
          message.error(`Erro ao carregar cursos do usuário ${user.full_name}`);
        }
      }

      setLinkedUsers(newLinkedUsers);
    } catch (error: any) {
      message.error(error.response?.data?.message || "Erro ao carregar cursos");
    } finally {
      setLoadingBulkCourses(false);
      setLoadingLinkedUsers(false);
    }
  };

  useEffect(() => {
    if (isVisible && selectedUsers.length > 0) {
      handleLoadCoursesData();
    } else {
      setSelectedCoursesForBulk([]);
      setLinkedUsers({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, selectedUsers]);

  const handleBulkCourseAssignment = async () => {
    if (selectedCoursesForBulk.length === 0) {
      message.warning("Selecione pelo menos um curso");
      return;
    }

    logsRef.current = [];

    const usuariosValidos = selectedUsers
      .map((userId) => {
        const user = users.find((u) => u.id === userId);
        return user?.user_twygo_id ? user : null;
      })
      .filter((user) => user !== null) as User[];

    const totalOperacoesPossiveis =
      usuariosValidos.length * selectedCoursesForBulk.length;
    let totalOperacoesRealizadas = 0;

    const atualizarProgresso = () => {
      totalOperacoesRealizadas++;
      const novoProgresso = Math.min(
        Math.floor((totalOperacoesRealizadas / totalOperacoesPossiveis) * 100),
        99
      );
      updateProgress(novoProgresso);
    };

    onOpenLogsModal([], 0);

    setLoadingBulkCourses(true);
    setLoadingLinkedUsers(true);

    const newLinkedUsers: { [key: number]: string[] } = { ...linkedUsers };

    try {
      interface OperacaoVinculacao {
        user: User;
        courseId: number;
        courseName: string;
      }

      const operacoesNecessarias: OperacaoVinculacao[] = [];

      addLog("Analisando cursos e usuários...", "info");

      for (const user of usuariosValidos) {
        try {
          const userEventsResponse = await api.get(
            `/twygo/info-user-courses/${user.user_twygo_id}`
          );
          const userCourses = userEventsResponse.data.events || [];
          const userCourseIds = userCourses.map(
            (event: UserEvent) => event.event_id
          );

          for (const courseId of selectedCoursesForBulk) {
            if (!userCourseIds.includes(courseId)) {
              const courseName =
                availableCoursesForBulk.find((c) => c.content_id === courseId)
                  ?.name || `Curso ID ${courseId}`;

              operacoesNecessarias.push({
                user,
                courseId,
                courseName,
              });
            }
          }

          if (
            !selectedCoursesForBulk.some(
              (courseId) => !userCourseIds.includes(courseId)
            )
          ) {
            addLog(
              `${user.full_name} já possui todos os cursos selecionados`,
              "info"
            );
            for (let i = 0; i < selectedCoursesForBulk.length; i++) {
              atualizarProgresso();
            }
          }
        } catch (error) {
          addLog(
            `Erro ao verificar cursos do usuário ${user.full_name}`,
            "error"
          );
          for (let i = 0; i < selectedCoursesForBulk.length; i++) {
            atualizarProgresso();
          }
        }
      }

      if (operacoesNecessarias.length === 0) {
        addLog("Todos os usuários já possuem os cursos selecionados", "info");
        updateProgress(100);
        setLoadingBulkCourses(false);
        setLoadingLinkedUsers(false);
        return;
      }

      addLog(
        `Total de ${operacoesNecessarias.length} vinculações serão realizadas`,
        "info"
      );

      let operacaoAtual = 0;
      for (const operacao of operacoesNecessarias) {
        operacaoAtual++;

        try {
          await api.post("/twygo/add-course", {
            user_twygo_id: operacao.user.user_twygo_id,
            event_id: operacao.courseId,
          });

          addLog(
            `Curso "${operacao.courseName}" vinculado com sucesso para ${operacao.user.full_name} (${operacaoAtual}/${operacoesNecessarias.length})`,
            "success"
          );

          if (!newLinkedUsers[operacao.courseId]) {
            newLinkedUsers[operacao.courseId] = [];
          }
          if (
            !newLinkedUsers[operacao.courseId].includes(
              operacao.user.full_name || ""
            )
          ) {
            newLinkedUsers[operacao.courseId].push(
              operacao.user.full_name || ""
            );
          }
        } catch (error: any) {
          addLog(
            `Erro ao vincular curso "${operacao.courseName}" ao usuário ${
              operacao.user.full_name
            }: ${error.response?.data?.message || "Erro desconhecido"}`,
            "error"
          );
        }

        atualizarProgresso();
      }

      setLinkedUsers(newLinkedUsers);

      addLog("Processo de vinculação concluído!", "success");
      updateProgress(100);
    } catch (error: any) {
      addLog(
        `Erro geral: ${error.response?.data?.message || "Erro desconhecido"}`,
        "error"
      );
      updateProgress(100);
    } finally {
      setLoadingBulkCourses(false);
      setLoadingLinkedUsers(false);
    }
  };

  const userColumns = [
    {
      title: "Nome",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
  ];

  const selectedUsersData = selectedUsers.map((userId) => {
    const user = users.find((u) => u.id === userId);
    return {
      key: userId,
      full_name: user?.full_name || "",
      email: user?.email || "",
    };
  });

  const courseColumns = [
    {
      title: "ID",
      dataIndex: "content_id",
      key: "content_id",
      width: "15%",
    },
    {
      title: "Nome do Curso",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Usuários Vinculados",
      key: "linkedUsers",
      render: (_: any, record: any) => {
        const courseLinkedUsers = linkedUsers[record.content_id] || [];
        return loadingLinkedUsers ? (
          <Spin size="small" />
        ) : courseLinkedUsers.length > 0 ? (
          <Tooltip
            title={
              <div>
                <strong>Usuários com este curso:</strong>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  {courseLinkedUsers.map((userName, index) => (
                    <li key={index}>{userName}</li>
                  ))}
                </ul>
              </div>
            }
          >
            <span>{courseLinkedUsers.length} usuários vinculados</span>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
  ];

  return (
    <StyledDrawer
      title="Vincular Cursos em Massa"
      width={800}
      onClose={onClose}
      visible={isVisible}
      maskClosable={false}
      destroyOnClose
      zIndex={1000}
      footer={
        <FooterContainer>
          <Button onClick={onClose} type="default">
            Cancelar
          </Button>
          <Button
            onClick={handleBulkCourseAssignment}
            loading={loadingBulkCourses}
            type="primary"
          >
            Vincular Cursos
          </Button>
        </FooterContainer>
      }
    >
      <Container>
        <UsersListContainer>
          <UsersListTitle>
            Usuários Selecionados
            <UsersCount>({selectedUsers.length})</UsersCount>
          </UsersListTitle>
          <StyledTable
            columns={userColumns}
            dataSource={selectedUsersData}
            pagination={false}
            size="small"
            scroll={{ y: 200 }}
          />
        </UsersListContainer>

        <CoursesContainer>
          <CoursesTitle>Selecione os Cursos</CoursesTitle>
          {loadingBulkCourses ? (
            <LoadingContainer>
              <Spin size="default" tip="Carregando cursos..." />
            </LoadingContainer>
          ) : availableCoursesForBulk.length === 0 ? (
            <EmptyContainer>
              <Empty description="Nenhum curso disponível" />
            </EmptyContainer>
          ) : (
            <StyledTable
              rowSelection={{
                selectedRowKeys: selectedCoursesForBulk,
                onChange: (selectedRowKeys) => {
                  setSelectedCoursesForBulk(selectedRowKeys as number[]);
                },
              }}
              dataSource={availableCoursesForBulk}
              columns={courseColumns}
              rowKey="content_id"
              pagination={false}
              scroll={{ y: 300 }}
            />
          )}
        </CoursesContainer>
      </Container>
    </StyledDrawer>
  );
};

export default TwygoMassiveCourses;
